// client/src/pages/admin/ManageUserTypes.js
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import adminService from '../../services/adminService.js';
import Button from '../../components/ui/Button.js';
import Input, { Textarea } from '../../components/ui/Input.js';
import Modal from '../../components/ui/Modal.js';

export default function ManageUserTypes() {
  const [userTypes, setUserTypes] = useState([]);
  const [planDeliveryTypes, setPlanDeliveryTypes] = useState([]);
  const [sendingDomains, setSendingDomains] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Default trial tier setting
  const [defaultTrialTier, setDefaultTrialTier] = useState('0');
  const [savingDefault, setSavingDefault] = useState(false);

  const fetchTypes = async () => {
    try {
      const data = await adminService.getUserTypes();
      setUserTypes(data.userTypes);
    } catch { /* ignore */ }
  };

  const fetchPlanDeliveryTypes = async () => {
    try {
      const data = await adminService.getPlanDeliveryTypes();
      setPlanDeliveryTypes(data.planDeliveryTypes);
    } catch { /* ignore */ }
  };

  const fetchSendingDomains = async () => {
    try {
      const data = await adminService.getActiveSendingDomains();
      setSendingDomains(data.sendingDomains || []);
    } catch { /* ignore */ }
  };

  const fetchDefaultTrialTier = async () => {
    try {
      const data = await adminService.getSettings();
      const setting = (data.settings || []).find(s => s.settingkey === 'default_trial_tier');
      if (setting) setDefaultTrialTier(setting.settingvalue);
    } catch { /* ignore */ }
  };

  useEffect(() => { 
    fetchTypes(); 
    fetchPlanDeliveryTypes();
    fetchSendingDomains();
    fetchDefaultTrialTier();
  }, []);

  const handleEdit = (ut) => {
    setEditing({
      ...ut,
      features: (ut.features || []).join('\n'),
      plandeliverytypeid: ut.plandeliverytypeid || 1,
      unlimitedcontacts: ut.maxcontacts === -1,
      noexpiration: ut.cardexpirydays === 0,
      trial_enabled: ut.trial_enabled || false,
      trial_duration_days: ut.trial_duration_days || 30,
      trial_mode: ut.trial_mode || 'duration',
      trial_fixed_deadline: ut.trial_fixed_deadline ? new Date(ut.trial_fixed_deadline).toISOString().split('T')[0] : '',
      sending_domain_id: ut.sending_domain_id || '',
      // Coerce to a strict boolean so a NULL from an un-migrated row
      // renders the checkbox as unchecked rather than indeterminate.
      simple_email_form: ut.simple_email_form || false,
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const planType = parseInt(editing.plandeliverytypeid);
      const payload = {
        ...editing,
        features: editing.features.split('\n').map((f) => f.trim()).filter(Boolean),
        pricemonthly: parseFloat(editing.pricemonthly) || 0,
        priceyearly: parseFloat(editing.priceyearly) || 0,
        maxcontacts: editing.unlimitedcontacts ? -1 : (editing.maxcontacts === '' || editing.maxcontacts === null ? 10 : parseInt(editing.maxcontacts)),
        maxdeliveriesmonth: editing.maxdeliveriesmonth === '' || editing.maxdeliveriesmonth === null ? 0 : parseInt(editing.maxdeliveriesmonth),
        maxdeliverytotalfreeaccount: editing.maxdeliverytotalfreeaccount === '' || editing.maxdeliverytotalfreeaccount === null ? 0 : parseInt(editing.maxdeliverytotalfreeaccount),
        maxuserimages: editing.maxuserimages === '' || editing.maxuserimages === null ? 0 : parseInt(editing.maxuserimages),
        cardexpirydays: editing.noexpiration ? 0 : (editing.cardexpirydays === '' || editing.cardexpirydays === null ? 7 : parseInt(editing.cardexpirydays)),
        displayorder: parseInt(editing.displayorder) || 0,
        plandeliverytypeid: planType,
        trial_enabled: editing.trial_enabled || false,
        trial_duration_days: parseInt(editing.trial_duration_days) || 0,
        trial_mode: editing.trial_mode || 'duration',
        trial_fixed_deadline: editing.trial_fixed_deadline || null,
        cangroups: editing.cangroups || false,
        canimportgoogle: editing.canimportgoogle || false,
        simple_email_form: editing.simple_email_form || false,
        sending_domain_id: editing.sending_domain_id || null,
      };
      delete payload.unlimitedcontacts;
      delete payload.noexpiration;
      await adminService.updateUserType(editing.usertypeid, payload);
      setMessage('Saved!');
      setEditing(null);
      fetchTypes();
    } catch (err) {
      setMessage(err.data?.error || 'Save failed');
    }
    setSaving(false);
  };

  const handleSaveDefaultTrialTier = async () => {
    setSavingDefault(true);
    try {
      await adminService.updateSettings([{ settingkey: 'default_trial_tier', settingvalue: String(defaultTrialTier) }]);
      setMessage('Default trial tier saved!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err.data?.error || 'Failed to save');
    }
    setSavingDefault(false);
  };

  const boolField = (label, key) => (
    <label className="flex items-center gap-2 text-sm text-royal-200">
      <input
        type="checkbox"
        checked={editing[key] || false}
        onChange={(e) => setEditing({ ...editing, [key]: e.target.checked })}
        className="rounded"
      />
      {label}
    </label>
  );

  const getDeliveryLimitDisplay = (ut) => {
    if (ut.plandeliverytypeid === 3) return 'Unlimited';
    if (ut.plandeliverytypeid === 2) return `${ut.maxdeliverytotalfreeaccount || 0} lifetime`;
    return `${ut.maxdeliveriesmonth || 0}/mo`;
  };

  const getContactsDisplay = (ut) => {
    if (ut.maxcontacts === -1) return 'Unlimited';
    return ut.maxcontacts?.toLocaleString();
  };

  const getExpiryDisplay = (ut) => {
    if (ut.cardexpirydays === 0) return 'Never expires';
    return `${ut.cardexpirydays} days`;
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="mb-5">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Dashboard
        </Link>
      </div>

      <h1 className="font-display text-3xl text-gold mb-6">Manage Plans & Tiers</h1>

      {message && (
        <div className="mb-4 p-3 bg-royal-800 border border-gold/30 rounded-lg text-gold text-sm">
          {message}
        </div>
      )}

      {/* ─── Auto-Trial on Registration ──────────────────── */}
      <div className="bg-royal-800/40 border border-royal-600 rounded-xl p-5 mb-8">
        <h2 className="text-lg text-gold font-display mb-3">Auto-Trial on Registration</h2>
        <p className="text-royal-200 text-sm mb-4">When a new user signs up, automatically start them on a trial for this tier. Set to "None" to let users choose from the pricing page instead.</p>
        
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-white text-sm mb-1.5">Default Trial Tier</label>
            <select
              value={defaultTrialTier}
              onChange={(e) => setDefaultTrialTier(e.target.value)}
              className="w-full bg-royal-800 border border-royal-600 rounded-lg px-3 py-2.5 text-white text-base focus:border-gold focus:outline-none"
            >
              <option value="0">None (user chooses from pricing page)</option>
              {userTypes.filter(ut => ut.trial_enabled).map((ut) => (
                <option key={ut.usertypeid} value={ut.usertypeid}>
                  {ut.typename} ({ut.trial_duration_days} day trial)
                </option>
              ))}
            </select>
          </div>
          <Button onClick={handleSaveDefaultTrialTier} disabled={savingDefault}>
            {savingDefault ? 'Saving...' : 'Save'}
          </Button>
        </div>

        {defaultTrialTier === '0' && (
          <p className="text-royal-200 text-sm mt-2">Users will see "Start Free Trial" buttons on tiers that have trials enabled below.</p>
        )}
      </div>

      {/* ─── Plan Cards ──────────────────────────────────── */}
      <div className="space-y-4">
        {userTypes.map((ut) => (
          <div key={ut.usertypeid} className="bg-royal-800/40 border border-royal-600 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-white font-semibold text-lg">{ut.typename}</h3>
                <p className="text-royal-200 text-sm">{ut.tagline}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-sm px-2 py-1 rounded-full ${ut.isenabled ? 'bg-green-900/40 text-green-400' : 'bg-red-900/40 text-red-400'}`}>
                  {ut.isenabled ? 'Enabled' : 'Disabled'}
                </span>
                <Button variant="outline" className="text-sm py-1 px-3" onClick={() => handleEdit(ut)}>
                  Edit
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <span className="text-royal-200">Pricing:</span>{' '}
                <span className="text-royal-100">
                  {ut.ispaid ? `$${parseFloat(ut.pricemonthly).toFixed(2)}/mo · $${parseFloat(ut.priceyearly).toFixed(2)}/yr` : 'Free'}
                </span>
              </div>
              <div>
                <span className="text-royal-200">Contacts:</span>{' '}
                <span className="text-royal-100">{getContactsDisplay(ut)}</span>
              </div>
              <div>
                <span className="text-royal-200">Deliveries:</span>{' '}
                <span className="text-royal-100">{getDeliveryLimitDisplay(ut)}</span>
              </div>
              <div>
                <span className="text-royal-200">Expiry:</span>{' '}
                <span className="text-royal-100">{getExpiryDisplay(ut)}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-2">
              {ut.cansend && <span className="text-sm bg-royal-700 text-royal-200 px-2 py-0.5 rounded">Send</span>}
              {ut.canschedule && <span className="text-sm bg-royal-700 text-royal-200 px-2 py-0.5 rounded">Schedule</span>}
              {ut.canuselinks && <span className="text-sm bg-royal-700 text-royal-200 px-2 py-0.5 rounded">Links</span>}
              {ut.canviewanalytics && <span className="text-sm bg-royal-700 text-royal-200 px-2 py-0.5 rounded">Analytics</span>}
              {ut.canuploadcsv && <span className="text-sm bg-royal-700 text-royal-200 px-2 py-0.5 rounded">CSV</span>}
              {ut.autobirthday && <span className="text-sm bg-royal-700 text-royal-200 px-2 py-0.5 rounded">Birthday</span>}
              {ut.cangroups && <span className="text-sm bg-royal-700 text-royal-200 px-2 py-0.5 rounded">Groups</span>}
              {ut.canimportgoogle && <span className="text-sm bg-royal-700 text-royal-200 px-2 py-0.5 rounded">Google Import</span>}
              {ut.simple_email_form && <span className="text-sm bg-purple-900/40 text-purple-300 px-2 py-0.5 rounded">Simple Email Form</span>}
              {ut.plandeliverytypeid === 2 && <span className="text-sm bg-amber-900/40 text-amber-300 px-2 py-0.5 rounded">Lifetime Limit</span>}
              {ut.plandeliverytypeid === 3 && <span className="text-sm bg-green-900/40 text-green-400 px-2 py-0.5 rounded">Unlimited Deliveries</span>}
              {ut.maxcontacts === -1 && <span className="text-sm bg-green-900/40 text-green-400 px-2 py-0.5 rounded">Unlimited Contacts</span>}
              {ut.cardexpirydays === 0 && <span className="text-sm bg-green-900/40 text-green-400 px-2 py-0.5 rounded">Never Expires</span>}
              {ut.trial_enabled && <span className="text-sm bg-gold/20 text-gold px-2 py-0.5 rounded">Trial: {ut.trial_duration_days}d</span>}
              {ut.sending_domain && <span className="text-sm bg-blue-900/40 text-blue-300 px-2 py-0.5 rounded">📤 {ut.sending_domain}</span>}
            </div>

            {ut.stripepriceidmonthly && (
              <p className="text-royal-200 text-sm mt-2 font-mono">
                Stripe: {ut.stripepriceidmonthly?.slice(0, 20)}...
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      <Modal isOpen={!!editing} onClose={() => setEditing(null)} title={`Edit: ${editing?.typename}`} maxWidth="max-w-3xl">
        {editing && (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-3">
              <Input label="Type Name" value={editing.typename} onChange={(e) => setEditing({ ...editing, typename: e.target.value })} />
              <Input label="Tagline" value={editing.tagline || ''} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })} />
            </div>
            <Input label="Display Order" type="number" value={editing.displayorder} onChange={(e) => setEditing({ ...editing, displayorder: e.target.value })} />

            {/* Toggles */}
            <div>
              <p className="text-royal-200 text-sm font-semibold uppercase tracking-wider mb-2">Plan Settings</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {boolField('Paid Plan', 'ispaid')}
                {boolField('Enabled', 'isenabled')}
                {boolField('Popular Badge', 'ispopular')}
              </div>
            </div>

            {/* Feature Flags */}
            <div>
              <p className="text-royal-200 text-sm font-semibold uppercase tracking-wider mb-2">Feature Flags</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {boolField('Can Send', 'cansend')}
                {boolField('Can Schedule', 'canschedule')}
                {boolField('Can Use Links', 'canuselinks')}
                {boolField('Can View Analytics', 'canviewanalytics')}
                {boolField('Can Upload CSV', 'canuploadcsv')}
                {boolField('Auto Birthday', 'autobirthday')}
                {boolField('Can Add Groups', 'cangroups')}
                {boolField('Can Import Google', 'canimportgoogle')}
              </div>
            </div>

            {/* Email Form Style */}
            <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-lg">
              <p className="text-purple-300 text-sm font-semibold uppercase tracking-wider mb-3">Email Form Style</p>
              <label className="flex items-start gap-3 text-sm text-white cursor-pointer">
                <input
                  type="checkbox"
                  checked={editing.simple_email_form || false}
                  onChange={(e) => setEditing({ ...editing, simple_email_form: e.target.checked })}
                  className="mt-0.5 w-5 h-5 rounded border-royal-600 bg-royal-800 text-gold focus:ring-gold/50"
                />
                <span>
                  <span className="block font-medium">Use the simplified email form</span>
                  <span className="block text-royal-200 text-sm mt-1 leading-relaxed">
                    When ON, the "let us email it for you" section in the share flow hides
                    contacts, groups, and the contact picker. Instead it shows a single
                    <strong className="text-white"> Send to</strong> name + email and a
                    from-name field — best for casual users sending one card at a time.
                    When OFF, users get the full contacts/groups recipient manager
                    (default; best for business tiers). Scheduling and birthday auto-send
                    still appear in both modes, gated by the flags above.
                  </span>
                </span>
              </label>
            </div>

            {/* Delivery Limit Type */}
            <div>
              <p className="text-royal-200 text-sm font-semibold uppercase tracking-wider mb-2">Delivery Limit Type</p>
              <select
                value={editing.plandeliverytypeid || 1}
                onChange={(e) => setEditing({ ...editing, plandeliverytypeid: parseInt(e.target.value) })}
                className="w-full bg-royal-800 border border-royal-600 rounded-lg px-3 py-2.5 text-white text-base focus:border-gold focus:outline-none"
              >
                {planDeliveryTypes.map((pdt) => (
                  <option key={pdt.plantypeid} value={pdt.plantypeid}>
                    {pdt.typename}
                  </option>
                ))}
              </select>
              <p className="text-royal-200 text-sm mt-1">
                {editing.plandeliverytypeid === 3
                  ? 'Unlimited deliveries — no limit enforced.'
                  : editing.plandeliverytypeid === 2 
                  ? 'Free accounts get a total lifetime delivery limit (not monthly).' 
                  : 'Paid accounts get a monthly delivery limit that resets each month.'}
              </p>
            </div>

            {/* Limits */}
            <div>
              <p className="text-royal-200 text-sm font-semibold uppercase tracking-wider mb-2">Limits</p>
              <div className="grid grid-cols-2 gap-3">
                {/* Contacts */}
                <div>
                  <label className="flex items-center gap-2 text-sm text-royal-200 mb-1.5">
                    <input
                      type="checkbox"
                      checked={editing.unlimitedcontacts || false}
                      onChange={(e) => setEditing({ ...editing, unlimitedcontacts: e.target.checked })}
                      className="rounded"
                    />
                    Unlimited Contacts
                  </label>
                  {!editing.unlimitedcontacts && (
                    <Input 
                      label="Max Contacts" 
                      type="number" 
                      value={editing.maxcontacts === -1 ? '' : editing.maxcontacts} 
                      onChange={(e) => setEditing({ ...editing, maxcontacts: e.target.value })} 
                    />
                  )}
                  {editing.unlimitedcontacts && (
                    <p className="text-green-400 text-sm py-2.5">Contacts: Unlimited</p>
                  )}
                </div>
                
                {/* Deliveries */}
                {editing.plandeliverytypeid === 2 ? (
                  <Input 
                    label="Max Deliveries (Lifetime Total)" 
                    type="number" 
                    value={editing.maxdeliverytotalfreeaccount || ''} 
                    onChange={(e) => setEditing({ ...editing, maxdeliverytotalfreeaccount: e.target.value })} 
                    placeholder="e.g., 5"
                  />
                ) : editing.plandeliverytypeid === 3 ? (
                  <div>
                    <label className="block text-sm text-royal-200 mb-1.5">Deliveries</label>
                    <p className="text-green-400 text-sm py-2.5">Unlimited — no limit enforced</p>
                  </div>
                ) : (
                  <Input 
                    label="Max Deliveries/Month" 
                    type="number" 
                    value={editing.maxdeliveriesmonth || ''} 
                    onChange={(e) => setEditing({ ...editing, maxdeliveriesmonth: e.target.value })} 
                    placeholder="e.g., 100"
                  />
                )}
                
                <Input 
                  label="Max Images" 
                  type="number" 
                  value={editing.maxuserimages} 
                  onChange={(e) => setEditing({ ...editing, maxuserimages: e.target.value })} 
                />

                {/* Card Expiry */}
                <div>
                  <label className="flex items-center gap-2 text-sm text-royal-200 mb-1.5">
                    <input
                      type="checkbox"
                      checked={editing.noexpiration || false}
                      onChange={(e) => setEditing({ ...editing, noexpiration: e.target.checked })}
                      className="rounded"
                    />
                    Cards Never Expire
                  </label>
                  {!editing.noexpiration && (
                    <Input 
                      label="Card Expiry (days)" 
                      type="number" 
                      value={editing.cardexpirydays} 
                      onChange={(e) => setEditing({ ...editing, cardexpirydays: e.target.value })} 
                    />
                  )}
                  {editing.noexpiration && (
                    <p className="text-green-400 text-sm py-2.5">eCards: Never expire</p>
                  )}
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div>
              <p className="text-royal-200 text-sm font-semibold uppercase tracking-wider mb-2">Pricing (Display)</p>
              <div className="grid grid-cols-2 gap-3">
                <Input label="Monthly Price ($)" type="number" step="0.01" value={editing.pricemonthly} onChange={(e) => setEditing({ ...editing, pricemonthly: e.target.value })} />
                <Input label="Yearly Price ($)" type="number" step="0.01" value={editing.priceyearly} onChange={(e) => setEditing({ ...editing, priceyearly: e.target.value })} />
              </div>
            </div>

            {/* Stripe */}
            <div>
              <p className="text-royal-200 text-sm font-semibold uppercase tracking-wider mb-2">Stripe Price IDs</p>
              <div className="grid grid-cols-1 gap-3">
                <Input label="Monthly Price ID" value={editing.stripepriceidmonthly || ''} onChange={(e) => setEditing({ ...editing, stripepriceidmonthly: e.target.value })} placeholder="price_1ABC..." />
                <Input label="Yearly Price ID" value={editing.stripepriceidyearly || ''} onChange={(e) => setEditing({ ...editing, stripepriceidyearly: e.target.value })} placeholder="price_1DEF..." />
              </div>
            </div>

            {/* Features list */}
            <div>
              <p className="text-royal-200 text-sm font-semibold uppercase tracking-wider mb-2">Feature List (for pricing page, one per line)</p>
              <Textarea
                value={editing.features}
                onChange={(e) => setEditing({ ...editing, features: e.target.value })}
                rows={5}
                placeholder="100 contacts&#10;100 deliveries/month&#10;Add photos"
              />
            </div>

            {/* Disabled message */}
            <Input
              label="Disabled Message (shown when tier is off)"
              value={editing.disabledmessage || ''}
              onChange={(e) => setEditing({ ...editing, disabledmessage: e.target.value })}
              placeholder="This plan is temporarily unavailable."
            />

            {/* Per-Tier Free Trial */}
            <div className="p-4 bg-gold/5 border border-gold/20 rounded-lg">
              <p className="text-gold text-sm font-semibold uppercase tracking-wider mb-3">Free Trial</p>
              
              <label className="flex items-center gap-2 text-sm text-white mb-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editing.trial_enabled || false}
                  onChange={(e) => setEditing({ ...editing, trial_enabled: e.target.checked })}
                  className="w-5 h-5 rounded border-royal-600 bg-royal-800 text-gold focus:ring-gold/50"
                />
                Enable free trial for this tier
              </label>

              {editing.trial_enabled && (
                <div className="space-y-3 ml-7">
                  <div>
                    <label className="block text-white text-sm mb-1.5">Trial Duration (days)</label>
                    <input
                      type="number"
                      value={editing.trial_duration_days || 30}
                      onChange={(e) => setEditing({ ...editing, trial_duration_days: e.target.value })}
                      min={1}
                      max={365}
                      className="w-full bg-royal-800 border border-royal-600 rounded-lg px-3 py-2.5 text-white text-base focus:border-gold focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-white text-sm mb-1.5">Trial Mode</label>
                    <select
                      value={editing.trial_mode || 'duration'}
                      onChange={(e) => setEditing({ ...editing, trial_mode: e.target.value })}
                      className="w-full bg-royal-800 border border-royal-600 rounded-lg px-3 py-2.5 text-white text-base focus:border-gold focus:outline-none"
                    >
                      <option value="duration">Duration (X days from activation)</option>
                      <option value="fixed_date">Fixed Date (everyone expires same day)</option>
                      <option value="both">Both (whichever comes first)</option>
                    </select>
                  </div>

                  {(editing.trial_mode === 'fixed_date' || editing.trial_mode === 'both') && (
                    <div>
                      <label className="block text-white text-sm mb-1.5">Fixed Trial Deadline</label>
                      <input
                        type="date"
                        value={editing.trial_fixed_deadline || ''}
                        onChange={(e) => setEditing({ ...editing, trial_fixed_deadline: e.target.value })}
                        className="w-full bg-royal-800 border border-royal-600 rounded-lg px-3 py-2.5 text-white text-base focus:border-gold focus:outline-none"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sending Domain */}
            <div className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg">
              <p className="text-blue-400 text-sm font-semibold uppercase tracking-wider mb-3">Email Sending Domain</p>
              <p className="text-royal-200 text-sm mb-3">
                eCard notification emails from users on this tier will be sent from this domain. 
                This helps isolate email reputation between tiers.
              </p>
              <select
                value={editing.sending_domain_id || ''}
                onChange={(e) => setEditing({ ...editing, sending_domain_id: e.target.value || null })}
                className="w-full bg-royal-800 border border-royal-600 rounded-lg px-3 py-2.5 text-white text-base focus:border-gold focus:outline-none"
              >
                <option value="">Use default / fallback domain</option>
                {sendingDomains.map((d) => (
                  <option key={d.domainid} value={d.domainid}>
                    {d.domain} — {d.from_email}
                  </option>
                ))}
              </select>
              {sendingDomains.length === 0 && (
                <p className="text-royal-400 text-sm mt-2">
                  No sending domains configured. <a href="/admin/sending-domains" className="text-gold hover:underline">Add one →</a>
                </p>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}